/**
 * StaffHoldNotices: the booking sheet's hold lines and refusals (CC 0366, 0368) in the same words,
 * for any screen that books through useStaffHold. Holding · held until · just taken · no longer
 * held · busy, with "Choose another time" and "Book anyway".
 */
import { Button, T } from "@v0y4ge/jrni-ui";
import { useT } from "@v0y4ge/i18n-runtime";
import type { StaffHoldApi } from "./use-staff-hold";

const COLLISION_LABEL: Record<string, string> = {
  booking: "Another appointment", hold: "A time another staff member is holding", time_off: "Time off",
  staff_override: "A closure in their schedule", event: "An event they host or work", queue_duty: "Queue duty",
  calendar_busy: "Busy in their connected calendar",
};

export default function StaffHoldNotices({ sh, picked, staffName, timezone, onChooseAnother, onBookAnyway, anywayDisabled, testid = "staff-hold" }: {
  sh: StaffHoldApi; picked: boolean; staffName?: string | null; timezone?: string | null;
  onChooseAnother: () => void; onBookAnyway: () => void; anywayDisabled: boolean; testid?: string;
}) {
  const t = useT();
  const { hold, holding, refusal, acked } = sh;
  const fmt = (iso: string) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? iso : new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit", ...(timezone ? { timeZone: timezone } : {}) }).format(d); };
  return (
    <>
      {picked && holding ? <div role="status" style={{ fontSize: 12.5, color: T.soft }}>{t("appointments.sheet.holding", "Holding this time…")}</div> : null}
      {picked && hold && !holding && refusal?.kind !== "busy" ? (
        <div data-testid={testid + "-held"} style={{ fontSize: 12.5, color: T.n2 }}>
          {t("appointments.sheet.heldFor", "Held for you until {time}{staff}.", { time: new Date(hold.expiresAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }), staff: hold.staffName ? " · " + hold.staffName : "" })}
          {acked ? " " + t("appointments.sheet.bookingAnyway", "You chose to book although they're busy.") : ""}
        </div>
      ) : null}
      {refusal?.kind === "taken" ? (
        <div role="alert" data-testid={testid + "-taken"} style={{ padding: "9px 12px", borderRadius: 8, background: "#FEF3F2", border: "1px solid #FECDCA", fontSize: 12.5, color: "#B42318" }}>{refusal.message || t("appointments.sheet.justTaken", "This time was just taken by another staff member. Choose another time.")}</div>
      ) : null}
      {refusal?.kind === "lapsed" ? (
        <div role="alert" data-testid={testid + "-lapsed"} style={{ padding: "9px 12px", borderRadius: 8, background: "#FFFAEB", border: "1px solid #FEDF89", fontSize: 12.5, color: "#B54708" }}>{t("appointments.sheet.holdLapsed", "This time is no longer held for you. Choose a time again.")}</div>
      ) : null}
      {refusal?.kind === "busy" ? (
        <div role="alert" data-testid={testid + "-busy"} style={{ padding: "12px 14px", borderRadius: 10, background: "#FFFAEB", border: "1px solid #FEDF89", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#7A2E0E" }}>
            {t("appointments.sheet.busyTitle", "{name} is busy at this time", { name: hold?.staffName || staffName || t("appointments.sheet.thisStaff", "This staff member") })}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "#7A2E0E", lineHeight: 1.55 }}>
            {refusal.collisions.map((c, i) => <li key={i}>{COLLISION_LABEL[c.kind] ?? c.kind}{c.ref ? " " + c.ref : ""} · {fmt(c.start)}–{fmt(c.end)}</li>)}
            {refusal.count > refusal.collisions.length ? <li>{t("appointments.sheet.busyMore", "and {n} more", { n: refusal.count - refusal.collisions.length })}</li> : null}
          </ul>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="secondary" size="sm" onClick={onChooseAnother} data-testid={testid + "-busy-other"}>{t("appointments.sheet.chooseAnother", "Choose another time")}</Button>
            <Button size="sm" disabled={anywayDisabled} onClick={onBookAnyway} data-testid={testid + "-book-anyway"}>{t("appointments.sheet.bookAnyway", "Book anyway")}</Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
